import { CaretDownOutlined, PlayCircleOutlined, PlusOutlined, EyeOutlined, VerticalAlignBottomOutlined } from '@ant-design/icons';
import { Button, Collapse, Tooltip, Alert } from 'antd';
import Vocabulary from 'components/Vocabulary';
import _, { isNull, omitBy, isFunction } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import IntegrationsAPI from 'restAPI/IntegrationsAPI';
import ModalUtil from 'utils/modalUtil';
import { CellRenderers, Sorters, columnRenderArrayData, ColumnFilterTypes } from 'utils/tableUtils';
import { getOperationalStatusFilterText, PullToolSuspendSwitch } from './common';
import { PanelHeader } from './common/PullBasedTools';
import ToolsIntegrationsAPI, {ToolConfigurationNotesAPI} from 'restAPI/ToolsIntegrationAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import AuthorisedContainer from 'components/AuthorisedContainer';
import message from "components/CustomMessage";
import ActionMenuV2 from 'components/ActionMenuV2';
import WebhookModel from './WebhookModel';
import ContentOverlay from 'components/ContentOverlay';
import { deleteCIConfig, showCIConfig, getCIActionOptions } from 'views/Integrations/CIConfiguration/CIConfiguration';
import TableContainerEvolved from 'components/TableContainerEvolved';
import { HelpIcon, DownloadIcon } from 'components/Icons';
import { triggerStartScanTool, getFortifyScanActionOptions } from 'views/Integrations/fortify/fortifyStartScan';
import { toolsName } from 'views/GlobalSettings/ToolsName';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext'
import { Link } from 'react-router-dom';
import useUrlFilters from 'hooks/useUrlFilters';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import ToolConfigurationNotesPopup from 'components/ToolConfigurationNotesPopup';
import ToolConfigurationNotes from "components/ToolConfigurationNotes";
import { SCAN_EXECUTE_SUPPORTED_TOOL } from 'views/ToolSettings/Settings/SettingStatusConstant';
import UserAPI from 'restAPI/UserAPI';
import NotesNotification from 'components/NotesNotification';

const MULTI_COMPONENT_SUPPORTED_TOOLS = ['Mend', 'Traceable', 'GitHub Secret Scan', 'Dependabot', 'Spectral', 'Fortify', 'Aqua', 'GitGuardian', 'Contrast Security', 'Qualys Infrastructure', 'Edgescan', 'Veracode', "Halo Security", "Acunetix", "Deepfence", "Gitlab Security", "Mend.io", "Tenable Webapp", "Netsparker", "AppCheck", "Orca Security", "Checkmarx SCA", "RiskRecon", "Privado", "Prisma Cloud Redlock", "Dependency Track", "Burpsuite Enterprise", "Sonarcloud"];
const LAST_ASSESSMENT_DATE_SUPPORTED_TOOLS = ['Mend', 'Fortify', 'Edgescan', "Gitlab Security", "Semgrep","SonarQube","Veracode","SysDig","Blackduck Hub","Checkmarx","Qualys WebApp", "Rapid7 InsightVM", "RiskRecon","Prisma Cloud Twistlock", "Dependency Track", "Coverity on Polaris"]

const loginConfigObj = {
    'total_configurations': 'Configurations Count',
    'apiEndpoint': 'Endpoint', 
    'username': 'Username',
    'name': 'Configuration Name'
}

export default function GenericPullConfigListing({ loginConfigList, extraLoginConfigFields, triggerModifyParam, ...restProps }) {
    if(_.isEmpty(loginConfigList)){
        return restProps.noDataMessage;
    }
    return loginConfigList.map((loginConfig, i) => (
        <ConfigItem 
            loginConfig={loginConfig} 
            key={i} 
            index={i} 
            extraLoginConfigFields={extraLoginConfigFields} 
            triggerModifyParam={triggerModifyParam} 
            {...restProps} 
        />
    ))
}

function ConfigItem({loginConfig, index, extraLoginConfigFields, triggerModifyParam, ...restProps}){
    const elementRef = useRef();
    const tableRef = useRef();
    const [ data, setData ] = useState([]); 
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const globalSettings = useGlobalSettingsContext();
    const [exportFilters, setExportFilters] = useState({});
    const urlFilters = useUrlFilters();
    const [users, setUsers] = useState(undefined);
    const [activeToolConfigIds, setActiveToolConfigIds] = useState([]);
    const [notesCountStats, setNotesCountStats] = useState({});

    const defaultFilter = useMemo(() => {
        if (restProps.defaultActiveLoginConfigId === loginConfig.id) {
            return {
                product_name: urlFilters.defaultProductFilter,
                sub_product_name: urlFilters.defaultSubproductFilter,
                environment: urlFilters.defaultEnvironmentFilter,
                operationalStatus: !_.isEmpty(urlFilters.defaultOperationalStatusFilter) ? urlFilters.defaultOperationalStatusFilter : undefined,
            };
        }
        return _.isEmpty(urlFilters) ? {} : {
            product_name: urlFilters.product,
            sub_product_name: urlFilters.subProduct,
            environment: urlFilters.environmentName,
        };
    }, [
        restProps.defaultActiveLoginConfigId,
        loginConfig.id,
        urlFilters,
    ]);

    useEffect(()=>{
        const { cancel, promise } = UserAPI.getAll(undefined);
        const cancelPromise = cancel;
        promise
            .then((usersList) => {
                setUsers(usersList)
            })
            .catch((err) => {
                console.log(err);
            })
        return () => cancelPromise();
    },[]);

    const onViewNote = useCallback(async (toolConfigId, activeNoteId=undefined) => {
        if(users!==undefined){
            ModalUtil.show({
                content: <ToolConfigurationNotes toolConfigId={toolConfigId} activeNoteId={activeNoteId} users= {users}/>,
            })
        }
      }, [users]);

    useEffect(() => {
        // bring the config into view, target config can be outside the screen when large configs present
        if (restProps.defaultActiveLoginConfigId === loginConfig.id) {
            elementRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [restProps.defaultActiveLoginConfigId, loginConfig.id]);

    const fetchToolConfigUnreadNotesCount = useCallback(() => {
        const data = {
            toolConfigIds: activeToolConfigIds
        }
        const { promise, cancel } = ToolConfigurationNotesAPI.getToolConfigurationNotesUnreadStats(loginConfig.id, data);
        promise.then((response) => {
            const loginConfigNotesCountStats = response["data"];
            if(loginConfigNotesCountStats){
                setNotesCountStats(loginConfigNotesCountStats?.loginConfigNotesCountStats);
            }
        });
        return cancel;
    }, [activeToolConfigIds, loginConfig.id]);

    useEffect(() => {
        if(activeToolConfigIds.length>0){
            let cancel = fetchToolConfigUnreadNotesCount();
            const intervalId = setInterval(() => {
                cancel = fetchToolConfigUnreadNotesCount();
            }, 2 * 60 * 1000); // 2mins
        
            return () => {
                clearInterval(intervalId);
                if (isFunction(cancel)) {
                    cancel();
                }
            };
        }
    }, [activeToolConfigIds, fetchToolConfigUnreadNotesCount]);


    const modifyActiveToolConfigIdList = (data)=>{
        if(!data?.length){
            setActiveToolConfigIds([]);
        }
    }

    const updateActiveCollapsesToolConfigs = (dataSource) => {
        const toolConfigIdsList = dataSource?.map(item => item.id);
        if(toolConfigIdsList){
            setActiveToolConfigIds(toolConfigIdsList);
        }
    }

    const setToolData = (data) => {
         setData(data);
    }

    const onAddProject = useCallback(async (record) => {
        const MappingForm = restProps.MappingForm;
        ModalUtil.show({
            content: <MappingForm toolName={restProps.toolName} onUpdateConfigComponent={restProps.onUpdateConfigComponent} loginConfig={loginConfig} loginConfigId={loginConfig.id} record={record}/>,
            afterClose: () => {
                tableRef.current.refresh();
                tableRef.current.refreshFilters();
            }
        })
    }, [loginConfig, restProps.onUpdateConfigComponent, restProps.MappingForm,restProps.toolName]);

    const onAddNote = useCallback(async (record) => {
      ModalUtil.show({
        content: <ToolConfigurationNotesPopup
                  okText={"Add"}
                  title={"Add Note"}
                  type={null}
                  note
                  noteLength={1000}
                  users = {users}
                  onOk={async (data) => {
                    await ToolConfigurationNotesAPI.createToolConfigurationNote(record.id, data).promise;
                  }}
              />
      })
    }, [users]);

    useEffect(() => {
        if(urlFilters.defaultNoteId && urlFilters.defaultActiveMappingId && restProps.defaultActiveLoginConfigId === loginConfig.id){
            onViewNote(urlFilters.defaultActiveMappingId[0], urlFilters.defaultNoteId[0]);
        }
    }, [urlFilters.defaultActiveMappingId, urlFilters.defaultNoteId, onViewNote, restProps.defaultActiveLoginConfigId, loginConfig.id]);

    const editConfig = useCallback(() => {
        const CredentialsForm = restProps.CredentialsForm;
        ModalUtil.show({
            content: <CredentialsForm config={loginConfig} name={restProps.toolName} />,
            afterClose: (success) => {
                const onUpdateConfigComponent = restProps.onUpdateConfigComponent;
                if(success){
                    onUpdateConfigComponent && onUpdateConfigComponent(true);
                }
                tableRef.current.refresh();
                tableRef.current.refreshFilters();
            }
        })
    }, [loginConfig, restProps.CredentialsForm, restProps.toolName, restProps.onUpdateConfigComponent]);

    const onDelete = useCallback(async () => {
      const response = await ToolsIntegrationsAPI.deleteGenericLoginDetail(restProps.toolName, loginConfig.id).promise
      message.success(response)
      setTimeout(() => restProps.onUpdateConfigComponent(), 50)
    }, [loginConfig.id, restProps]);


    const exportData = useCallback(async (type) => {
        const activeColumns = tableRef.current.getActiveColumns();

        Object.keys(loginConfigObj).forEach(key => {
          if(Object.keys(loginConfig).indexOf(key) !== -1){
            activeColumns.unshift({ field: key, header : loginConfigObj[key]})
          }
        })

        const productIndex = _.findIndex(activeColumns, ['header', 'product_name']);
        const subProductIndex = _.findIndex(activeColumns, ['header', 'sub_product_name']);
        const suspendedIndex = _.findIndex(activeColumns, ['header', 'Enable']);
        const notesIndex = _.findIndex(activeColumns, ['field', 'notes'])

        if (productIndex !== -1) {
            activeColumns[productIndex].header = `${globalSettings['product']} Names`
            activeColumns[productIndex].field = 'product_name'
        }

        if (subProductIndex !== -1) {
            activeColumns[subProductIndex].header = `${globalSettings['subProduct']} Name`
            activeColumns[subProductIndex].field = 'sub_product_name'
        }

        if (suspendedIndex !== -1) {
            activeColumns[suspendedIndex].header = `Disable`
        }

        if(notesIndex !== -1) {
            activeColumns[notesIndex].header = 'Notes'
        }
        else {
            activeColumns.splice(activeColumns.length-1, 0, {field: "notes", header: "Notes"})
        }

        try {
            const closeFn = message.loading("Preparing CSV");
            const data = await ToolsIntegrationsAPI.exportSecurityToolConfigData({..._.omit(exportFilters, ['page', 'size']), exportFields: activeColumns }, restProps.toolName).promise;
            closeFn();
            const { presignedUrl: url } = data
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            console.error(e)
        } 
    }, [loginConfig, globalSettings, exportFilters, restProps.toolName]);
    
    const deleteMessage = loginConfig.total_configurations > 0 ? "Please delete the configurations of this account first" : "Delete credential";
    let noteFromConfig = "This config is suspended";
    noteFromConfig = ((loginConfig.note?.length > 0) ? noteFromConfig + ": " +loginConfig.note : noteFromConfig) + ". Please contact ArmorCode support.";
    
    const onClick = async (_, record) => {
      switch(record.name){
        case "edit":
          editConfig()
          break;
        case "delete":
            await onDelete()
          break;
        case "export":
            exportData()
          break;
        default:
          break;
      }
    }
    const items = [{
      type: "edit"
    },
    {
        type: "export",
        text: "Export",
        icon: <DownloadIcon />,
        disabled: loginConfig.total_configurations === 0,
        permission: PermissionSetMap.WRITE_TOOLS
    },
    {
      type: "delete",
      permission: PermissionSetMap.DELETE_TOOL,
      tooltip: deleteMessage,
      disabled: loginConfig.total_configurations > 0 || loginConfig.shareable
    }]
    return (
        <Collapse
            className="pull-based-tool"
            defaultActiveKey={restProps.defaultActiveLoginConfigId}
            // defaultActiveKey={index === 0 ? loginConfig.id : undefined}
            bordered={false}
            expandIconPosition="right"
            onChange={(collapseData)=>{modifyActiveToolConfigIdList(collapseData)}}
            expandIcon={({ isActive }) => <Tooltip title={isActive ? "Close Mapping" : "Open Mapping" }><CaretDownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 20 }} /></Tooltip>}
        >
            <Collapse.Panel key={loginConfig.id} header={<PanelHeader loginConfig={loginConfig}  extraFields={extraLoginConfigFields} index={index} />}>
           <div className='d-flex justify-content-between'>
                <div>
                    {loginConfig.status === "SUSPENDED" && <Alert message={noteFromConfig} type="error"/>}
                </div>
                <div className="d-flex align-items-center justify-content-end m-b-sm">
                    <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
                        <Button className="m-l-sm" type="primary" size="small" onClick={() => onAddProject()}>Add {restProps.componentName || restProps.displayComponentName || ""} Mapping</Button>
                    </AuthorisedContainer>
                    <div className='m-l-sm'>
                        <ActionMenuV2 items={items} onClick={onClick} />
                    </div>
                </div>
            </div>
                <div ref={elementRef}>
                    <ConfigTable 
                        loginConfig={loginConfig} 
                        data={data} 
                        setToolData={setToolData} 
                        {...restProps} 
                        tableEvolvedRef={tableRef}  
                        onEditProject={onAddProject} 
                        onViewNote={onViewNote}
                        onAddNote={onAddNote}
                        selectedRowKeys={selectedRowKeys} 
                        setRowKeys={setSelectedRowKeys} 
                        setExportDataFilters={setExportFilters} 
                        triggerModifyParam={triggerModifyParam}
                        defaultFilter={defaultFilter}
                        updateActiveCollapsesToolConfigs={updateActiveCollapsesToolConfigs}
                        notesCountStats={notesCountStats}
                    />
                </div>
            </Collapse.Panel>
        </Collapse>
    )
}

function ConfigTable({
    data,
    loginConfig,
    suspendAPI,
    deleteAPI,
    onUpdateConfigComponent,
    onEditProject,
    onAddNote,
    onViewNote,
    componentName,
    componentKey,
    showOrganisationColumn,
    organisationColumnDisplayName,
    toolName,
    extraColumns,
    setToolData,
    tableEvolvedRef,
    selectedRowKeys,
    setRowKeys,
    setExportDataFilters,
    triggerModifyParam,
    defaultFilter,
    appendColumns = [],
    updateActiveCollapsesToolConfigs,
    notesCountStats,
}) {
    const featureFlags = useFeatureFlag();
    const configColumns = _.isFunction(extraColumns) ? extraColumns(loginConfig) : extraColumns                 //(ENG-32760) sending extraColumns as function as we need to show repositories column on the basis of loginConfig data

    const updateConfig = useCallback(() => {
        onUpdateConfigComponent();
        tableEvolvedRef.current.refresh();
        tableEvolvedRef.current.refreshFilters();
    },[onUpdateConfigComponent, tableEvolvedRef])

    const onDelete = useCallback(async (data) => {
        if (deleteAPI) {
            const { promise } = deleteAPI(data.tool_name, data.id)
            promise.then(() => {
                updateConfig()
                message.success('Configuration deleted successfully.');
            })
            return promise
        }
    }, [deleteAPI, updateConfig]);

    const onTrigger = async (record) => {
        const { product_name, sub_product_name, engagementId, environment, applicationName, applicationId, imageName, id } = record
        let request = {
            product: product_name,
            subproduct: sub_product_name,
            engagementId,
            env: environment,
            applicationName,
            projectId: applicationId,
            imageName,
            toolConfigId: id
        }
        if(_.isFunction(triggerModifyParam)){
            request = {...request, ...triggerModifyParam(record)}
        }
        await IntegrationsAPI.triggerConfiguration(request, record.tool_name).promise;
        message.success("Scan initiated successfully. You can check the status under Scans page in a while.")
    }

    const onExecuteScan = useCallback((configId) => {
        const closeMessage = message.loading("Executing Scan...");
        const { promise, cancel } = ToolsIntegrationsAPI.executeScanTool(configId)
        promise.then(res => {
          message.success(res.data)
        })
        .catch(e => console.error(e))
        .finally(() => closeMessage())
        return cancel;
      },[])

    const onFetchComponents = async (record) => {
        const {
          product_name,
          sub_product_name,
          engagementId,
          environment,
          applicationName,
          applicationId,
          imageName,
          id
        } = record

        let request = {
          product: product_name,
          subproduct: sub_product_name,
          engagementId,
          env: environment,
          applicationName,
          projectId: applicationId,
          imageName,
          toolConfigId: id,
          executionType: "SBOM"
        }

        if(_.isFunction(triggerModifyParam)){
          request = {...request, ...triggerModifyParam(record)}
        }

        await IntegrationsAPI.triggerConfiguration(request, record.tool_name).promise;
        message.success("Component fetch initiated. You can check the status under Scans page in a while.")
      }

    const onActionClick = async (name, record) => {
        switch(record.name){
          case "startScan":
            !record.suspended && onTrigger(record.data)               
            break;
          case "fetchComponents":
            !record.suspended && onFetchComponents(record.data)
            break;
          case "edit":
            onEditProject(record.data)                  
            break;
          case "addNote":
            onAddNote(record.data)
            break;
          case "viewNote":
            onViewNote(record.data.id)
            break;
          case "delete":
            await onDelete(record.data)                  
            break;  
          case "webhook":
                WebhookPopup(record.data.id)
            break;
            case "configure":
            case "edit_configuration":
              showCIConfig(record, updateConfig);
              break;
            case "delete_configuration":
              deleteCIConfig(record, updateConfig);
              break;
            case "start_scanTool":
              triggerStartScanTool(record, updateConfig);
            break;
            case "executeScan":
                onExecuteScan(record.data.id);
            break;
          default:
            break
        }
      }

      function WebhookPopup(id){
        ModalUtil.show({
            content: (
              <WebhookModel id={id} toolName={toolName} />
        )})  
    }

    const prependColumns = [
        ...(showOrganisationColumn ? [{
            title: organisationColumnDisplayName || 'Organization',
            dataIndex: 'orgName',
            width:150,
            sorter: Sorters.STRING('orgName'),
            key: 'orgName',
            showFilter: true,
            render: CellRenderers.VALUE_OR_NA
        }] : []),
    
        ...(configColumns ? 
            configColumns.map((column) => ({
                render: (record) => {
                    if (MULTI_COMPONENT_SUPPORTED_TOOLS.indexOf(toolName) !== -1) {
                        return columnRenderArrayData(record)
                    }
                    return <ContentOverlay content={CellRenderers.VALUE_OR_NA(record)} minHeight={40}/>
                },
                showFilter: true,
                ...column,
            }))
        : [])
    ];

    const lastAssessmentDateSupportedTools = [...LAST_ASSESSMENT_DATE_SUPPORTED_TOOLS];

    if (featureFlags.NOWSECURE_LAST_SCAN_DATE_SUPPORT) {
        lastAssessmentDateSupportedTools.push("NowSecure");
    }

    const columns = [
        ...prependColumns,
        ...(componentName ? [{
                title: componentName,
                dataIndex: componentKey,
                key:componentKey,
                render: (record) => {
                    if (MULTI_COMPONENT_SUPPORTED_TOOLS.indexOf(toolName) !== -1) {
                        return columnRenderArrayData(record)
                    }
                    return <ContentOverlay content={CellRenderers.VALUE_OR_NA(record)} minHeight={40}/>
                },
                showFilter: true,
                width: 130
        }] : []),
    {
        title: <Vocabulary name="product" />,
        dataIndex: "product_name",
        sorter: Sorters.STRING("product"),
        className: "break-all",
        width: 120,
        key: "product_name",
        showSort: true,
        showFilter: true,
        render: (text, record) => record.product_id ? (
            <Link to={`/product/${record.product_id}`} title={text.product_name}>
              {text}
            </Link>
        ) : text
    }, {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "sub_product_name",
        sorter: Sorters.STRING("subproduct"),
        width: 120,
        key: "sub_product_name",
        className: "break-all",
        showSort: true,
        showFilter: true,
        render: (text, record) => record.sub_product_id ? (
            <Link to={`/subProduct/${record.sub_product_id}`} title={text.sub_product_name}>
              {text}
            </Link>
        ) : text,
    }, {
        title: "Environment",
        dataIndex: "environment",
        width: 130,
        sorter: Sorters.STRING("environment"),
        key: "environment",
        showSort: true,
        showFilter: true,
        render:  (environment) => <EnvironmentName name = {environment} />,
        filterLabelFormatter: CellRenderers.VOCABULARY_RENDERER,
    },{
        title: <>Latest Ingestion Status <Tooltip title={"N/A status represents that no reports were found in the tool for that particular project"}><HelpIcon /></Tooltip></>,
        dataIndex: "scanStatus",
        key: "scanStatus",
        width: 90,
        showFilter: true,
        render: CellRenderers.SCAN_STATUS,
        filterLabelFormatter: CellRenderers.SCAN_STATUS
    },{
        title: <>Latest Ingestion Success <Tooltip placement="top" title="Last time ArmorCode successfully connected to the tool to get finding data" ><HelpIcon /> </Tooltip> </>,
        dataIndex: "lastConnected",
        key: "lastConnected",
        width: 110,
        sorter: Sorters.NUMBER("lastConnected"),
        showFilter: true,
        showSort: true,
        filterType: ColumnFilterTypes.DATE_RANGE,
        filterLabelFormatter: CellRenderers.DATE,
        render: CellRenderers.TIME_RELATIVE,
    },{
        title:<>Latest Finding Data <Tooltip placement="top" title="Last time ArmorCode got new/updated data from the tool" ><HelpIcon /> </Tooltip> </>,
        dataIndex: "scanLastRun",
        key: "scanLastRun",
        width: 100,
        sorter: Sorters.NUMBER("scanLastRun"),
        showFilter: true,
        showSort: true,
        filterType: ColumnFilterTypes.DATE_RANGE,
        filterLabelFormatter: CellRenderers.DATE,
        render: CellRenderers.TIME_RELATIVE

    },
    //hidden
    // {
    //     title: <>Latest Ingestion Failure <Tooltip title={"Last time ArmorCode failed to connect to the tool to get finding data"}><HelpIcon /></Tooltip></>,
    //     dataIndex: "latestIngestionFailureTime",
    //     key: "latestIngestionFailureTime",
    //     width: 120,
    //     sorter: Sorters.NUMBER("latestIngestionFailureTime"),
    //     showFilter: true,
    //     showSort: true,
    //     filterType: ColumnFilterTypes.DATE_RANGE,
    //     filterLabelFormatter: CellRenderers.DATE,
    //     render: CellRenderers.TIME_RELATIVE
    // }
    //  ,
     ...(lastAssessmentDateSupportedTools.indexOf(toolName) !== -1
        ?
        [{
            title:<>Last Tool Scan Date <Tooltip placement="top" title="Last time the tool ran a scan on this project" ><HelpIcon /> </Tooltip></>,
            dataIndex: "lastToolAssessmentDate",
            key: "lastToolAssessmentDate",
            width: 150,
            sorter: Sorters.NUMBER("lastToolAssessmentDate"),
            showSort: true,
            render: (lastToolAssessmentDate) => {
                if(_.isNil(lastToolAssessmentDate)) return CellRenderers.VALUE_OR_NA();
                const assessmentDetails = JSON.parse(lastToolAssessmentDate).lastToolAssessmentDate;
                return _.isEmpty(assessmentDetails) ? CellRenderers.VALUE_OR_NA() : CellRenderers.TIME(assessmentDetails[0].assessmentDate)
            }
        }] :
    []),
    ...appendColumns,
     {
        title: "Latest Connection Status",
        dataIndex: "operationalStatus",
        key:"operationalStatus",
        showFilter: true,
        width: 100,
        filterLabelFormatter: getOperationalStatusFilterText,
        render: CellRenderers.OPERATIONAL_STATUS,
    },
    {
        title: "Enable",
        dataIndex: "suspended",
        sorter: Sorters.STRING("suspended"),
        width: 90,
        render: (text, record) => (
            <PullToolSuspendSwitch text={text} record={record} suspendAPI={suspendAPI} onUpdateConfigComponent={onUpdateConfigComponent} tableRef={tableEvolvedRef} />
        )   
    },
    {
        title: "Note",
        key: "notes",
        dataIndex: "id",
        width: 50,
        render: (id, record) => {
            return <NotesNotification id={id} onViewNote={onViewNote} notesCountStats={notesCountStats[id]}/>
        }
    },
    {
        title: "Action",
        key: 'action',
        dataIndex: "id",
        width: 50,
        render: (id, record) => {
            const items = [{
                type: "startScan",
                permission: PermissionSetMap.WRITE_TOOLS,
                text: "Fetch Scan",
                icon: <VerticalAlignBottomOutlined />,
                disabled: record.suspended ? true : false,
                tooltip: record.suspended ? "Config Suspended" : "Start Scan Import",
              },
              ...(
                // DECOUPLE_SBOM_INGESTION flag was deleted in ENG-113807 (feature is now always on).
                // Keeping it here would permanently hide the "Fetch SBOM" button since the flag no longer exists.
                featureFlags.MANAGE_SBOM &&
                loginConfig.ingestSBOM === 'true'
                ?
                [{
                  type: "fetchComponents",
                  permission: PermissionSetMap.WRITE_TOOLS,
                  text: "Fetch SBOM",
                  icon: <VerticalAlignBottomOutlined />,
                  disabled: record.suspended ? true : false,
                  tooltip: record.suspended ? "Config Suspended" : "Fetch and import components",
                }]
                :
                []
              ),
              {
                type: "edit",
              },
              {
                type: "addNote",
                permission: PermissionSetMap.WRITE_TOOLS,
                text: "Add Note",
                icon: <PlusOutlined />,
              },
              {
                type: "viewNote",
                text: "View Note",
                icon: <EyeOutlined />,
              },
              {
                type: "delete",
                permission: PermissionSetMap.DELETE_TOOL,
                title: "Are you sure you want to delete this Config?",
                promptMessage: <>
                    <strong>
                        <p>You are deleting a mapping for <em>{loginConfig.name}</em> in <em>{toolName}</em>.</p>
                        <p>This will also:</p>
                        <ol>
                            <li>Delete All Related Scans</li>
                            <li>Delete All Related Findings</li>
                        </ol>
                        <p>Are you sure?</p>
                    </strong>
                 </>
              },
              ...(toolName === "HackerOne" ? [{
                type: "webhook",
                permission: PermissionSetMap.EDIT_TOOL_CONFIG,
              }] : [] ),
              ...(featureFlags.EXECUTE_TOOL_SCAN && SCAN_EXECUTE_SUPPORTED_TOOL.includes(toolName)
                ?
                [{
                    type: "executeScan",
                    permission: PermissionSetMap.WRITE_TOOLS,
                    text: "Execute Scan",
                    icon: <PlayCircleOutlined />,
                }]
                :
              [...getCIActionOptions(record)]),
              ...(toolName === toolsName.FORTIFY ?  getFortifyScanActionOptions(record) : [] )
            ]
    
            return <ActionMenuV2 items={items} onClick={onActionClick} data={record} />
        },
    }]


    const generateExtraFiltersFields =  useCallback((filters) => {
        const keys = [ ...(configColumns ? (configColumns.map(key => key.dataIndex)) : []), componentKey];
        
        if (showOrganisationColumn) {
            keys.push('orgName')
        }  

        let extraFilters={}
        keys.forEach(key => {
            extraFilters = { ...extraFilters,[key]:filters[key]};
            delete filters[key]
        })
        return { 
           filters: omitBy(filters, isNull),
           toolFilters: omitBy(extraFilters, isNull)           
       }
    },[componentKey,configColumns,showOrganisationColumn])


    const getAllPull = useCallback((params) => {
        [params.sortColumn, params.sortOrder] = (params.sort || "").split(",")
        if(params.sortColumn === "" ) {
            delete params.sortColumn
        }
        if(params.sortOrder === "") {
            delete params.sortOrder
        } 

        const obj = {
            ...params,
            toolType:"PULL",
            loginId:loginConfig.id,
            ...(generateExtraFiltersFields(params.filters))
        }
        setExportDataFilters(obj);
        return ToolsIntegrationsAPI.getGenericEvolvedConfigurationsByToolName(toolName,obj);
    },[loginConfig,toolName,generateExtraFiltersFields, setExportDataFilters])
    
    
    const getFilters = () =>{
        const obj = ToolsIntegrationsAPI.getGenericEvolvedFilteredConfigurationsByToolName(toolName,  {toolType:"PULL", loginId:loginConfig.id})
        obj.promise = obj.promise.then((data) => {
            return data;
        });
        setToolData(obj);
        return obj
    }

    return (
        <TableContainerEvolved
            className="no-header-style-override"
            api={getAllPull}
            filtersApi={getFilters}
            columns={_.forEach(columns, col => { if(col.title !== 'Action') col.alwaysDisplay = true })}
            enableScroll
            tableRef={tableRef => tableEvolvedRef.current = tableRef}
            defaultSort={{sortColumn: "createdAt", sortOrder: "descend"}}
            defaultFilter={defaultFilter}
            columnsReorder={true}
            disableURLUpdate={true}
            getData={(data) => data.configurations}
            updateActiveCollapsesToolConfigs = {updateActiveCollapsesToolConfigs}
        />
    )
}

